import React from "react";
import { Box, Chip, FormControl, FormLabel, Option, Sheet, Stack, Tooltip } from "@mui/joy";
import CommonModal from "../../common/CommonModal";
import DarkInput from "../../common/DarkInput";
import DarkSelect from "../../common/DarkSelect";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { SHourglass, SPlay, SStop } from "../../../styles/StyledMUI";
import { colorTokens } from "../../../styles/globalStyles";
import { useCustomer, useRental } from "../../../contexts/api";
import type {
  Rental,
  RentalLifeStatus,
  RentalModelPricing,
  RentalUpdateRequest,
} from "../../../api/rental/Rental.typed";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import {
  DEFAULT_TAX_RATE,
  TAX_RATES,
  formatNum,
  formatPrice,
  htToTtc,
  ttcToHt,
} from "../../billing/shared/billingUtils";
import {
  allowedRentalTransitions,
  rentalLifeStatusColor,
  rentalLifeStatusLabel,
  rentalTransitionBlock,
} from "../rentalStatusHelpers";
import RentalAddItemBlock, { buildKitSlots } from "./RentalAddItemBlock";

type Props = {
  open: boolean;
  rental: Rental | null;
  canEdit: boolean;
  onClose: () => void;
  onSave: (id: number, req: RentalUpdateRequest) => Promise<unknown>;
  onChanged: (id: number) => Promise<void> | void;
  onChangeStatus: (next: RentalLifeStatus) => Promise<void> | void;
  onReturnItem: (
    rentalID: number,
    itemID: number,
    state?: "returned" | "not_returned",
  ) => Promise<unknown>;
  onRemoveItem: (rentalID: number, itemID: number) => Promise<unknown>;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Convert ISO string to "YYYY-MM-DD" for date inputs (local calendar day).
function toDateInput(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Today as "YYYY-MM-DD" in the user's local timezone.
function todayDateInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Parse a "YYYY-MM-DD" input to an ISO timestamp at local noon (timezone-safe).
function fromDateInput(value: string): string | null {
  if (!value) return null;
  const [y, m, day] = value.split("-").map((p) => parseInt(p, 10));
  if (!y || !m || !day) return null;
  const d = new Date(y, m - 1, day, 12, 0, 0);

  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function startOfLocalDay(value: string): number {
  const d = new Date(value);

  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Inclusive day count: 15 → 17 = 3 days. Returns 1 if dates missing or invalid.
function computeDays(start?: string | null, end?: string | null): number {
  if (!start || !end) return 1;
  const s = startOfLocalDay(start);
  const e = startOfLocalDay(end);
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return 1;

  return Math.floor((e - s) / MS_PER_DAY) + 1;
}

export default function RentalEditModal({
  open,
  rental,
  canEdit,
  onClose,
  onSave,
  onChanged,
  onChangeStatus,
  onReturnItem,
  onRemoveItem,
}: Props) {
  const { getCustomersBatch } = useCustomer();
  const { listRentalModelPricing } = useRental();
  const [customer, setCustomer] = React.useState<ZekayCustomer | null>(null);
  const [unitPricing, setUnitPricing] = React.useState<RentalModelPricing[] | undefined>(undefined);

  const [startedAt, setStartedAt] = React.useState("");
  const [endedAt, setEndedAt] = React.useState("");
  const [priceHT, setPriceHT] = React.useState(0);
  const [ttcInput, setTtcInput] = React.useState("0");
  const [taxRate, setTaxRate] = React.useState<number>(DEFAULT_TAX_RATE);
  const [currency, setCurrency] = React.useState("EUR");
  const [notes, setNotes] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open || !rental) return;

    const today = todayDateInput();
    setStartedAt(toDateInput(rental.started_at) || today);
    setEndedAt(toDateInput(rental.ended_at) || today);
    setPriceHT(rental.price_ht);
    setTtcInput(formatNum(htToTtc(rental.price_ht, rental.tax_rate || DEFAULT_TAX_RATE)));
    setTaxRate(rental.tax_rate || DEFAULT_TAX_RATE);
    setCurrency(rental.currency || "EUR");
    setNotes(rental.notes ?? "");
  }, [open, rental]);

  React.useEffect(() => {
    if (!rental?.customer_id) {
      setCustomer(null);

      return;
    }

    let cancelled = false;
    (async () => {
      const list = await getCustomersBatch([rental.customer_id]);
      if (!cancelled) setCustomer(list?.[0] ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [rental?.customer_id, getCustomersBatch]);

  const isUnitMode = open && rental != null && rental.rental_product_id == null;

  React.useEffect(() => {
    if (!isUnitMode) {
      setUnitPricing(undefined);

      return;
    }

    let cancelled = false;
    (async () => {
      const list = await listRentalModelPricing();
      if (!cancelled) setUnitPricing(list ?? []);
    })();

    return () => {
      cancelled = true;
    };
  }, [isUnitMode, listRentalModelPricing]);

  if (!rental) return null;

  const items = rental.items ?? [];
  const isKitMode = rental.rental_product_id != null;
  const slots = isKitMode ? buildKitSlots(rental.rental_product, items) : [];
  const missingSlots = slots.filter((s) => s.got < s.expected);
  const kitComplete = slots.length > 0 && missingSlots.length === 0;
  const days = computeDays(
    fromDateInput(startedAt) ?? rental.started_at,
    fromDateInput(endedAt) ?? rental.ended_at,
  );
  const totalHT = isKitMode
    ? priceHT * days
    : items
        .filter((it) => !it.returned_at && it.unit_price_ht != null)
        .reduce((acc, it) => acc + (it.unit_price_ht ?? 0) * days, 0);
  const totalTTC = isKitMode
    ? htToTtc(priceHT, taxRate) * days
    : items
        .filter((it) => !it.returned_at && it.unit_price_ht != null)
        .reduce((acc, it) => acc + htToTtc(it.unit_price_ht ?? 0, it.tax_rate ?? 0) * days, 0);
  const totalCurrency = isKitMode
    ? currency
    : (items.find((it) => it.currency)?.currency ?? currency);
  const isLocked = !canEdit || rental.status === "returned" || rental.status === "cancelled";

  const customerName = customer
    ? [customer.FirstName, customer.LastName].filter(Boolean).join(" ").trim() ||
      `#${rental.customer_id}`
    : `#${rental.customer_id}`;

  const handleSave = async () => {
    setSubmitting(true);
    await onSave(rental.id, {
      price_ht: priceHT,
      tax_rate: taxRate,
      currency,
      started_at: fromDateInput(startedAt),
      ended_at: fromDateInput(endedAt),
      notes: notes.trim(),
    });
    setSubmitting(false);
    onClose();
  };

  const transitions = allowedRentalTransitions(rental.status);
  const blockActive = rentalTransitionBlock(rental, "active");
  const blockReturned = rentalTransitionBlock(rental, "returned");

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={`Location #${rental.id}`}
      subtitle={customerName}
      size="xl"
      fitContentHeight
      onConfirm={canEdit ? handleSave : undefined}
      confirmLabel="Enregistrer"
      loading={submitting}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="sm" variant="soft" color={rentalLifeStatusColor(rental.status)}>
            {rentalLifeStatusLabel(rental.status)}
          </Chip>
          {canEdit && transitions.includes("draft") && (
            <Tooltip title="Mettre en brouillon">
              <IconButtonCTA
                ctaVariant="neutral"
                size="sm"
                onClick={() => onChangeStatus("draft")}
                aria-label="Mettre la location en brouillon"
              >
                <SHourglass />
              </IconButtonCTA>
            </Tooltip>
          )}
          {canEdit && transitions.includes("active") && (
            <Tooltip title={blockActive ?? "Démarrer la location"}>
              <span>
                <IconButtonCTA
                  ctaVariant="success"
                  size="sm"
                  disabled={blockActive != null}
                  onClick={() => onChangeStatus("active")}
                  aria-label="Démarrer la location"
                >
                  <SPlay />
                </IconButtonCTA>
              </span>
            </Tooltip>
          )}
          {canEdit && transitions.includes("returned") && (
            <Tooltip title={blockReturned ?? "Clore — passer en retour"}>
              <span>
                <IconButtonCTA
                  ctaVariant="danger"
                  size="sm"
                  disabled={blockReturned != null}
                  onClick={() => onChangeStatus("returned")}
                  aria-label="Clore la location"
                >
                  <SStop />
                </IconButtonCTA>
              </span>
            </Tooltip>
          )}
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 1.5,
          }}
        >
          <FormControl>
            <FormLabel sx={{ color: colorTokens.text.muted }}>Début</FormLabel>
            <DarkInput
              type="date"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              disabled={isLocked}
            />
          </FormControl>

          <FormControl>
            <FormLabel sx={{ color: colorTokens.text.muted }}>Fin</FormLabel>
            <DarkInput
              type="date"
              value={endedAt}
              onChange={(e) => setEndedAt(e.target.value)}
              disabled={isLocked}
            />
          </FormControl>
        </Box>

        <Sheet
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "8px",
            p: 1.5,
          }}
        >
          {isKitMode ? (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
              >
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel sx={{ color: colorTokens.text.muted }}>HT / jour</FormLabel>
                  <DarkInput
                    type="number"
                    slotProps={{ input: { step: "0.01", min: "0" } }}
                    value={priceHT}
                    disabled={isLocked}
                    onChange={(e) => {
                      const ht = parseFloat(e.target.value) || 0;
                      setPriceHT(ht);
                      setTtcInput(formatNum(htToTtc(ht, taxRate)));
                    }}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel sx={{ color: colorTokens.text.muted }}>TTC / jour</FormLabel>
                  <DarkInput
                    type="number"
                    slotProps={{ input: { step: "0.01", min: "0" } }}
                    value={ttcInput}
                    disabled={isLocked}
                    onChange={(e) => {
                      setTtcInput(e.target.value);
                      const ttc = parseFloat(e.target.value) || 0;
                      setPriceHT(ttcToHt(ttc, taxRate));
                    }}
                  />
                </FormControl>
                <FormControl sx={{ width: { xs: "100%", sm: 120 } }}>
                  <FormLabel sx={{ color: colorTokens.text.muted }}>TVA</FormLabel>
                  <DarkSelect
                    value={taxRate}
                    disabled={isLocked}
                    onChange={(_, v) => {
                      if (v == null) return;
                      const rate = v as number;
                      setTaxRate(rate);
                      setTtcInput(formatNum(htToTtc(priceHT, rate)));
                    }}
                  >
                    {TAX_RATES.map((r) => (
                      <Option key={r.value} value={r.value}>
                        {r.label}
                      </Option>
                    ))}
                  </DarkSelect>
                </FormControl>
                <FormControl sx={{ width: { xs: "100%", sm: 100 } }}>
                  <FormLabel sx={{ color: colorTokens.text.muted }}>Devise</FormLabel>
                  <DarkSelect
                    value={currency}
                    disabled={isLocked}
                    onChange={(_, v) => v != null && setCurrency(v as string)}
                  >
                    <Option value="EUR">EUR</Option>
                    <Option value="USD">USD</Option>
                    <Option value="GBP">GBP</Option>
                    <Option value="CHF">CHF</Option>
                  </DarkSelect>
                </FormControl>
              </Stack>
              <DarkTypography level="body-sm" sx={{ mt: 1 }} colorVariant="title">
                {days} j · {formatPrice(totalHT, currency)} HT · {formatPrice(totalTTC, currency)}{" "}
                TTC
              </DarkTypography>
            </>
          ) : (
            <DarkTypography level="body-sm" colorVariant="title">
              Location à l'unité · {days} j · {formatPrice(totalHT, totalCurrency)} HT ·{" "}
              {formatPrice(totalTTC, totalCurrency)} TTC
            </DarkTypography>
          )}
        </Sheet>

        <FormControl>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Notes</FormLabel>
          <DarkInput value={notes} disabled={!canEdit} onChange={(e) => setNotes(e.target.value)} />
        </FormControl>

        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
            <DarkTypography level="title-sm">Panier</DarkTypography>
            {isKitMode && slots.length > 0 && (
              <Chip
                size="sm"
                variant="soft"
                color={kitComplete ? "success" : "warning"}
                title={
                  kitComplete
                    ? "Tous les équipements du kit sont présents"
                    : `Manque : ${missingSlots
                        .map((s) => `${s.modelName} (${s.got}/${s.expected})`)
                        .join(", ")}`
                }
              >
                {kitComplete ? "Kit complet" : "Kit incomplet"}
              </Chip>
            )}
            {!isKitMode && (
              <Chip size="sm" variant="soft" color="primary">
                À l'unité
              </Chip>
            )}
          </Stack>

          <RentalAddItemBlock
            rentalID={rental.id}
            rentalProduct={rental.rental_product ?? null}
            isKitMode={isKitMode}
            unitPricing={unitPricing}
            items={items}
            canEdit={canEdit && rental.status !== "returned" && rental.status !== "cancelled"}
            onChanged={() => onChanged(rental.id)}
            onReturnItem={onReturnItem}
            onRemoveItem={onRemoveItem}
          />
        </Box>
      </Stack>
    </CommonModal>
  );
}
